<script setup async>
import {competitionFacts} from '~/utils/privateAccess';
import {useHeadService} from '~/composables/useHeadService.js';
import {useAuthService} from '~/composables/useAuthService.js';
import {useHttpRequest} from "~/composables/useHttpRequest.js";
import {useValidation} from "~/composables/useValidation.js";
import {watch, onMounted, ref, computed, onUnmounted} from "vue";
import {createError} from "nuxt/app";
import _ from "lodash";
import {useMailer} from "~/composables/useMailer";
import FrontendBreadcrumbs from "~/components/navigation/FrontendBreadcrumbs.vue";
import TeamCard from "~/components/teams/TeamCard.vue";
import FrontendHeader from "~/components/layout/FrontendHeader.vue";
import LadderView from "~/components/ladders/LadderView.vue";
import FixtureCell from "~/components/fixtures/FixtureCell.vue";
import ChallengeWhenAndWhere from "~/components/ladders/ChallengeWhenAndWhere.vue";
import PlayingAreaSelect from "~/components/fields/PlayingAreaSelect.vue";
import {acceptTimeOptions, adminCancelPenaltyNote, buildAcceptRequest, challengeSchedule, buildSuggestionRequest, challengeSideName, expiryCountdown, isTeamSide, rejectionPenaltyText, suggestionSavedMessage, toMs} from "~/utils/challengeSchedule";
import {errorAlert} from "~/utils/adminActionCopy";
import {useFormData} from "~/composables/useFormData.js";
import {useSignalR} from "~/composables/useSignalR.js";
import SimpleDateTimeSelect from "~/components/fields/SimpleDateTimeSelect.vue";
import AvatarImageLoader from "~/components/loaders/AvatarImageLoader.vue";
import ArchivedNotice from "~/components/ArchivedNotice.vue";
import {useLocalisedDates} from "~/composables/useLocalisedDates";
import HandicapMatchBanner from "~/components/handicaps/HandicapMatchBanner.vue";
const {shortDate} = useLocalisedDates();

definePageMeta({
  name: 'LadderChallengeDetail',
  path: '/ladders/:ladderId/challenges/:ladderChallengeId',
});


const route = useRoute();
const router = useRouter();
const ladderId = route.params.ladderId;
const ladderChallengeId = route.params.ladderChallengeId;
const { on, off } = useSignalR();

const {
  item: ladderChallenge,
  getItem: getLadderChallenge,
  deleteItem: deleteLadderChallenge,
  updateItem: updateLadderChallenge,
  itemPending: ladderChallengePending,
  formPending: ladderChallengeFormPending,
  formErrors: ladderChallengeFormErrors,
  hasFormErrors: hasLadderChallengeFormErrors,
  hasItemErrors
} = useCrudApi("api/v2/ladderChallenges");
const {
  item: ladder,
  getItem: getLadder,
  itemPending: ladderPending,
  hasItemErrors: hasLadderErrors
} = useCrudApi("api/v2/ladders");
await getLadderChallenge(ladderChallengeId);
// The start this match plays off (ladder handicaps on): the fixture's frozen start once the
// challenge has one, otherwise the live pairing the API resolves on the challenge itself.
const challengeHandicapStart = computed(() =>
    ladderChallenge.value?.fixture?.handicapStart ?? ladderChallenge.value?.handicapStart ?? null);
if (hasItemErrors.value) {
  throw createError({
    statusCode: 404, // or another relevant status code
    message: 'Challenge not found. Has it been deleted?'
  });
}
await getLadder(ladderId);
if (hasLadderErrors.value) {
  throw createError({
    statusCode: 404, // or another relevant status code
    message: 'Ladder not found. Has it been deleted?'
  });
}


const {isSwapLadder} = useFormData();
const {t} = useI18n()
const {setMeta} = useHeadService();
setMeta(
    ladderChallenge.value?.clubName + ' / ' + ladderChallenge.value?.ladder.name + ' Challenge on Tap Leagues',
    ladderChallenge?.value?.clubName + ' ' + ladderChallenge?.value?.ladder.name + ' on Tap Leagues.'
);
const {isAuthenticated, user, logout, isAdminOfClub} = useAuthService();
const {outputDateFromDb, formatRange, inputDateForDb} = useCrudConverters();

const clubAdmin = computed(() => isAdminOfClub(ladderChallenge.value.clubId));

// The ladder has been deleted (archived) - show the deleted notice instead of content.
const ladderArchived = computed(() => !!ladder.value?.archivedAt);
usePrivatePage(computed(() => !!ladder.value?.privateToViewer || !!ladderChallenge.value?.privateToViewer));
const {httpRequest} = useHttpRequest();

// LOGIC FOR ADDING FIXTURE

const {notify, addListener, removeLocalListenersWithEvent} = useListenerService();

const {
  fixtureRequiresMyApproval,
  isPlayerChallengerOfChallenge,
  isPlayerChallengedInChallenge
} = useValidation();


const acceptLadderChallenge = ref(null);
// What the dialog is showing. The open state is "acceptLadderChallenge is not
// null", so closing sets it to null — and the dialog, still fading out, flipped
// to its other branch: accept a challenge and "Reject Challenge" flashed up.
// This remembers the last real mode so the content holds still while it closes.
const dialogAccepting = ref(true);
// Same for "on behalf": once accepted, the page's actions change under the
// closing dialog, so hold the label that was true when it opened.
const dialogOnBehalf = ref(false);
watch(acceptLadderChallenge, (mode) => {
  if (mode === null) return;
  dialogAccepting.value = mode;
  dialogOnBehalf.value = answeringOnBehalf.value;
});
const dialogTitle = computed(() => {
  const verb = dialogAccepting.value ? 'Accept' : 'Decline';
  return dialogOnBehalf.value ? `${verb} on ${challengedDisplayName.value}'s behalf` : `${verb} challenge`;
});
const acceptRejectLadderChallengeDialog = computed({
  get() {
    return acceptLadderChallenge.value !== null;
  },
  set(value) {
    if (value === false) {
      acceptLadderChallenge.value = null;
    }
  }
});

// A clock for everything time-dependent on the page (countdown, "time
// passed"), ticking with the countdown below.
const nowMs = ref(Date.now());

// What the challenger proposed, in words, for the accept dialog.
const proposedTimeText = computed(() => {
  const c = ladderChallenge.value;
  if (!c?.suggestedDate) return null;
  return outputDateFromDb(c.suggestedDate, 'ccc d MMM, HH:mm')
      + (c.suggestedPlayingAreaName ? ` at ${c.suggestedPlayingAreaName}` : '');
});
// The accept dialog's time choices (spec R2): agree (default) / another time /
// decide later when the challenger suggested one — never "agree" to a time that
// has passed — or an optional "Suggest a time" when they didn't.
const acceptOptions = computed(() => acceptTimeOptions(ladderChallenge.value, viewer.value, nowMs.value));
// Start every accept clean: the choice defaults to agreeing (what most people
// mean by pressing Accept, and they can see what they are agreeing to), the
// picker to the challenger's court.
watch(acceptLadderChallenge, (mode) => {
  if (mode !== true) return;
  const c = ladderChallenge.value;
  c.timeChoice = acceptOptions.value.defaultChoice;
  c.suggestDate = false;
  c.date = null;
  c.time = null;
  c.playingAreaId = c.suggestedPlayingAreaId ?? null;
  ladderChallengeFormErrors.value = {};
});

// Set default date/time when the accepter chooses to name their own time.
watch(() => [ladderChallenge.value.suggestDate, ladderChallenge.value.timeChoice], ([ticked, choice]) => {
  const newValue = ticked || choice === 'other';
  if (newValue && !ladderChallenge.value.date && !ladderChallenge.value.time) {
    const luxon = useNuxtApp().$luxon;
    const now = luxon.now();

    // Set date to tomorrow
    const tomorrow = now.plus({ days: 1 });
    ladderChallenge.value.date = tomorrow.toFormat('yyyy-MM-dd');

    // Round up to next half hour
    const minutes = now.minute;
    const roundedMinutes = Math.ceil(minutes / 30) * 30;
    const roundedTime = now.set({ minute: 0, second: 0, millisecond: 0 }).plus({ minutes: roundedMinutes });
    ladderChallenge.value.time = roundedTime.toFormat('HH:mm');
  }
});
const cancelLadderChallengeAction = async () => {
  await deleteLadderChallenge(ladderChallenge.value.id);
  if (!hasLadderChallengeFormErrors.value) {
    await router.push({path: `/ladders/${ladderId}`});
  }
}
const acceptOrRejectLadderChallengeAction = async () => {
  if (acceptLadderChallenge.value === true) {
    // The challenger proposed a time: send theirs back exactly as stored to
    // agree to it, another (with its court) to counter, or nothing to leave
    // theirs open. Without a proposal, an optional time of the accepter's own.
    const c = ladderChallenge.value;
    const opts = acceptOptions.value;
    const wantsOwnTime = opts.hasSuggestion ? c.timeChoice === 'other' : !!c.suggestDate;
    if (wantsOwnTime) {
      if (!c.date || !c.time) {
        ladderChallengeFormErrors.value = {global: ['Choose a date and a time, or pick another option.']};
        return;
      }
      const picked = toMs(inputDateForDb(`${c.date}T${c.time}`, "yyyy-MM-dd'T'HH:mm"));
      if (picked != null && picked < Date.now() - 5 * 60_000) {
        ladderChallengeFormErrors.value = {global: ['That time has already passed.']};
        return;
      }
    }
    const data = buildAcceptRequest(c, c,
        (date, time) => inputDateForDb(`${date}T${time}`, "yyyy-MM-dd'T'HH:mm"), nowMs.value);

    await updateLadderChallenge(ladderChallenge.value.id + "/accept", data);
    // The challenger changed the suggestion while this dialog was open: the
    // API refuses ("The suggested time has just changed / been removed — have another look."). Keep its message, reload the
    // challenge so the new time shows, and let them choose again.
    if (hasLadderChallengeFormErrors.value && /has just/i.test(ladderChallengeFormErrors.value?.global?.[0] ?? '')) {
      const errors = ladderChallengeFormErrors.value;
      const message = c.acceptanceMessage;
      await getLadderChallenge(c.id);
      ladderChallenge.value.acceptanceMessage = message;
      ladderChallenge.value.timeChoice = acceptOptions.value.defaultChoice;
      ladderChallenge.value.playingAreaId = ladderChallenge.value.suggestedPlayingAreaId ?? null;
      ladderChallengeFormErrors.value = errors;
      return;
    }
  } else {
    const data = {
      ladderChallengeId: ladderChallenge.value.id,
      rejectionReason: ladderChallenge.value.rejectionReason,
      accept: false
    }
    await updateLadderChallenge(ladderChallenge.value.id + "/accept", data);
  }
  if (!hasLadderChallengeFormErrors.value) {
    // The API says what the accept did to the time (agreed / counter / decide
    // later / suggestion passed / no time), on behalf or not; read defensively.
    const accepted = acceptLadderChallenge.value === true;
    setAlertMessage({
      message: accepted
          ? (ladderChallenge.value?.acceptOutcomeMessage || 'Challenge accepted')
          : 'Challenge declined',
      color: 'success',
    });
    acceptLadderChallenge.value = null;
  }
};


// Counts down to the challenge's own expiry (ladders set their own number of
// days — it was hard-coded to 7).
const countdown = ref('');
let intervalId;

const calculateCountdown = () => {
  nowMs.value = Date.now();
  countdown.value = expiryCountdown(ladderChallenge.value.expiresAt, nowMs.value) ?? '';
};

const onLadderChallengeSignal = () => {
    getLadderChallenge(ladderChallenge.value.id);
};

onMounted(() => {
  on(ladderChallenge.value.id,onLadderChallengeSignal)
  calculateCountdown();
  intervalId = setInterval(calculateCountdown, 1000);
  addListener("fixtureUpdated",(fixture) => {
    if (fixture.ladderChallengeId === ladderChallenge.value.id) {
      ladderChallenge.value.fixture = fixture;
      // a result entered from THIS page reads best on the fixture page (scores, match detail,
      // points) — go there once the dialog has closed, not underneath it
      if (fixture.homeTeamResult != null && (route.query.editingFixtureId || route.query.ladderChallengeId)) {
        pendingResultRedirect.value = fixture.id;
      }
    }
  })
});
const pendingResultRedirect = ref(null);
watch(() => [route.query.editingFixtureId, route.query.ladderChallengeId], ([editing, creating]) => {
  if (editing || creating || !pendingResultRedirect.value) return;
  const id = pendingResultRedirect.value;
  pendingResultRedirect.value = null;
  router.push(`/fixtures/${id}`);
});

onUnmounted(() => {
  clearInterval(intervalId);
  removeLocalListenersWithEvent("fixtureUpdated");
  off(ladderChallenge.value.id,onLadderChallengeSignal)
});

const {filtersToQuery} = useMailer();
const isChallengeAccepted = computed(() => {
  return ladderChallenge.value.acceptedAt;
});
const isChallengeResulted = computed(() => {
  return ladderChallenge.value.fixture && ladderChallenge.value.fixture.playedAt;
});
const {mdAndUp} = useDisplay();

/* ============================================================
   When & where + every action on the page, from one pure state
   function (utils/challengeSchedule.js — the app has the same).
   Nothing but an explicit Accept accepts a challenge: no schedule,
   result or live-score action on a pending one, for anyone.
   ============================================================ */
const viewer = computed(() => ({
  isChallenger: userRole.value === 'challenging',
  isChallenged: userRole.value === 'challenged',
  isAdmin: !!clubAdmin.value,
}));
const schedule = computed(() =>
    challengeSchedule(ladderChallenge.value, ladderChallenge.value.fixture ?? null, viewer.value, nowMs.value));
const hasAction = (id) => schedule.value.actions.some(a => a.id === id);
const actionLabel = (id) => schedule.value.actions.find(a => a.id === id)?.label;
// Side names from the API (ladder entry name, else team; doubles "A + B").
const challengedDisplayName = computed(() => challengeSideName(ladderChallenge.value, 'challenged', 'them'));
const challengerDisplayName = computed(() => challengeSideName(ladderChallenge.value, 'challenger', 'The challenger'));

const canCancel = computed(() => hasAction('cancel'));
// An admin cancelling an accepted challenge whose match exists but is unplayed.
const cancelDeletesMatch = computed(() => !!schedule.value.actions.find(a => a.id === 'cancel')?.deletesMatch);
// A club admin cancelling someone else's pending challenge is told it skips
// the ladder's decline penalty (only when the ladder has one).
const cancelMessage = computed(() => [
  'Are you sure you want to cancel this challenge?',
  adminCancelPenaltyNote(ladder.value, viewer.value, schedule.value.phase),
].filter(Boolean).join(' '));
// Their own answer (the challenged side, admin or not) or an admin's on behalf.
const canAnswer = computed(() => hasAction('accept') || hasAction('acceptOnBehalf'));
const answeringOnBehalf = computed(() => !hasAction('accept') && hasAction('acceptOnBehalf'));

// The dialog is told which button opened it, so its title and note match.
const scheduleRoute = (mode) => {
  const extra = mode ? {scheduleMode: mode} : {};
  if (ladderChallenge.value.fixture) {
    return {query: {schedulingFixtureId: ladderChallenge.value.fixture.id, ...extra}};
  }
  return {query: {schedulingLadderChallengeId: ladderChallenge.value.id, ...extra}};
};

// "Confirm time": one tap, sending the proposal back exactly as it is stored
// (and keeping its court) so it can never become a counter-proposal.
const {
  item: confirmedFixture,
  updateItem: updateFixtureSchedule,
  formPending: confirmPending,
  formErrors: confirmErrors,
  hasFormErrors: hasConfirmErrors,
} = useCrudApi('api/v2/fixtures/schedule');
const {setAlertMessage} = useAlertService();
const confirmTime = async (action) => {
  const fixtureId = ladderChallenge.value.fixture?.id;
  if (!fixtureId || !action?.value) return;
  try {
    await updateFixtureSchedule(fixtureId, {suggestedDate: action.value, playingAreaId: action.playingAreaId ?? null});
  } catch {
    // errors land in confirmErrors
  }
  if (hasConfirmErrors.value) {
    setAlertMessage(errorAlert(confirmErrors.value, 'The time could not be confirmed'));
    return;
  }
  setAlertMessage({message: 'Time confirmed', color: 'success'});
  notify('fixtureUpdated', confirmedFixture.value);
};
/* ---- The challenger changes (or sets, or removes) the suggested time while
   waiting. Never accepts; the challenged side is told. ---- */
const {
  updateItem: updateSuggestion,
  formPending: suggestionPending,
  formErrors: suggestionErrors,
  hasFormErrors: hasSuggestionErrors,
} = useCrudApi('api/v2/ladderChallenges');
const suggestionDialog = ref(false);
const suggestionAction = ref(null);
const suggestionForm = ref({date: null, time: null, playingAreaId: null});
const openSuggestionDialog = (action) => {
  const c = ladderChallenge.value;
  suggestionAction.value = action;
  suggestionErrors.value = {};
  const luxon = useNuxtApp().$luxon;
  // Start from the current suggestion if it is still ahead, else tomorrow at
  // the next half hour.
  const keep = c.suggestedDate && toMs(c.suggestedDate) > Date.now();
  const now = luxon.now();
  const slot = now.set({minute: 0, second: 0, millisecond: 0}).plus({minutes: Math.ceil(now.minute / 30) * 30});
  suggestionForm.value = {
    date: keep ? outputDateFromDb(c.suggestedDate, 'yyyy-MM-dd') : now.plus({days: 1}).toFormat('yyyy-MM-dd'),
    time: keep ? outputDateFromDb(c.suggestedDate, 'HH:mm') : slot.toFormat('HH:mm'),
    playingAreaId: c.suggestedPlayingAreaId ?? null,
  };
  suggestionDialog.value = true;
};
const saveSuggestion = async (remove = false) => {
  const c = ladderChallenge.value;
  const hadSuggestion = !!c.suggestedDate;
  const data = buildSuggestionRequest(suggestionForm.value,
      (date, time) => inputDateForDb(`${date}T${time}`, "yyyy-MM-dd'T'HH:mm"), {remove});
  if (!remove) {
    if (!data.suggestedDate) {
      suggestionErrors.value = {global: ['Choose a date and a time.']};
      return;
    }
    if (toMs(data.suggestedDate) < Date.now()) {
      suggestionErrors.value = {global: ['That time has already passed.']};
      return;
    }
  }
  try {
    await updateSuggestion(`${c.id}/suggestion`, data);
  } catch {
    // errors land in suggestionErrors
  }
  if (hasSuggestionErrors.value) return;
  suggestionDialog.value = false;
  setAlertMessage({message: suggestionSavedMessage(c, {removed: remove, hadSuggestion}), color: 'success'});
  await getLadderChallenge(c.id);
};

const onScheduleAction = (action) => {
  if (action.id === 'changeSuggestion') return openSuggestionDialog(action);
  if (action.id === 'confirmTime') return confirmTime(action);
  if (action.id === 'schedule') return router.push(scheduleRoute(action.mode));
};

const resultBtnText = computed(() => {
  if (isChallengeResulted.value) {
    const f = ladderChallenge.value.fixture;
    const mustApprove = isAuthenticated.value && fixtureRequiresMyApproval(f, {playerId: user.value?.playerId, isAdmin: !!clubAdmin.value});
    return mustApprove ? 'Review result' : 'View result';
  } else if (ladderChallenge.value.fixture && (ladderChallenge.value.fixture.homeTeamApprovedAt || ladderChallenge.value.fixture.awayTeamApprovedAt) && !ladderChallenge.value.fixture.approvedAt) {
    return 'Update/confirm result';
  } else {
    return 'Enter result';
  }
});
const resultRoute = computed(() => {
  // a resulted challenge VIEWS on the fixture page (scores, match detail, points);
  // editing stays in the dialog for un-resulted fixtures
  if (isChallengeResulted.value) {
    return `/fixtures/${ladderChallenge.value.fixture.id}`;
  }
  if (ladderChallenge.value.fixture) {
    return {query: {editingFixtureId: ladderChallenge.value.fixture.id}};
  }
  return {query: {ladderChallengeId: ladderChallenge.value.id}};
});

// Live scoring: available once the challenge is accepted and the ladder's scoring
// system supports it. An accepted challenge may have no fixture yet (no time was
// named), so starting live creates the (resultless) fixture first, then opens
// the counter.
const {
  createItem: createLiveFixture,
  item: liveFixtureItem,
  hasFormErrors: hasLiveStartErrors,
  formErrors: liveStartErrors,
  formPending: liveStartPending,
} = useCrudApi("api/v2/fixtures");
// Accepted (or played, to view/approve the result) only — never on a pending
// challenge: recording one would accept it without anyone pressing Accept.
const canRecordResult = computed(() => hasAction('recordResult'));
const canLiveScore = computed(() => {
  if (!hasAction('liveScore')) return false;
  if (isChallengeResulted.value) return false;
  return ladderChallenge.value.fixture
      ? ladderChallenge.value.fixture.supportsLiveScoring
      : (ladderChallenge.value.scoringSystem?.supportsLiveScoring ?? false);
});
const startLiveScoring = async () => {
  let fixtureId = ladderChallenge.value.fixture?.id;
  if (!fixtureId) {
    // same home/away convention as result entry: challenger at home
    // (createItem doesn't return the response — it lands in the crud instance's item)
    await createLiveFixture({
      ladderChallengeId: ladderChallenge.value.id,
      scoringSystemId: ladderChallenge.value.scoringSystem?.id
          ?? ladderChallenge.value.scoringSystemId
          ?? ladderChallenge.value.ladder?.scoringSystemId,
      homeTeamId: ladderChallenge.value.challengingTeamId,
      awayTeamId: ladderChallenge.value.challengedTeamId,
      approve: false,
    });
    if (hasLiveStartErrors.value) {
      setAlertMessage(errorAlert(liveStartErrors.value, 'Live scoring could not be started'));
      return;
    }
    fixtureId = liveFixtureItem.value?.id;
  }
  if (fixtureId) await navigateTo(`/fixtures/${fixtureId}/live`);
};

/* ============================================================
   Flutter parity — status machine + Progress card (ported from
   LadderChallengePage / _ChallengeStatusCard).
   ============================================================ */
const fixture = computed(() => ladderChallenge.value.fixture || null);
const dateAt = (d) => (d ? outputDateFromDb(d, "d MMM yyyy 'at' HH:mm") : '');
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const ordinal = (n) => {
  if (n == null) return '';
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  return `${n}${({1: 'st', 2: 'nd', 3: 'rd'})[n % 10] || 'th'}`;
};

const userRole = computed(() => {
  if (!isAuthenticated.value || !user.value.playerId) return 'readOnly';
  if (isPlayerChallengerOfChallenge(user.value.playerId, ladderChallenge.value)) return 'challenging';
  if (isPlayerChallengedInChallenge(user.value.playerId, ladderChallenge.value)) return 'challenged';
  return 'readOnly';
});
const isPlayer = computed(() => userRole.value !== 'readOnly');

const challengeStatus = computed(() => {
  const c = ladderChallenge.value;
  if (c.deletedAt) return 'cancelled';
  if (c.rejectedAt) return 'rejected';
  if (c.acceptedAt) return 'accepted';
  // API times are UTC without a zone: parse them as such, not as local time
  if (c.expiresAt && toMs(c.expiresAt) < nowMs.value) return 'expired';
  if (fixture.value) return 'skipped';
  return 'made';
});

const playedStatus = computed(() => {
  const f = fixture.value;
  if (!f) return 'inactive';
  if (f.homeTeamApprovedAt && f.awayTeamApprovedAt) return 'approved';
  const challengingIsHome = f.homeTeamId === ladderChallenge.value.challengingTeamId;
  if (f.homeTeamApprovedAt && !f.awayTeamApprovedAt) return challengingIsHome ? 'challengingApproved' : 'challengedApproved';
  if (!f.homeTeamApprovedAt && f.awayTeamApprovedAt) return challengingIsHome ? 'challengedApproved' : 'challengingApproved';
  return 'pending';
});

const challengingName = computed(() => userRole.value === 'challenging' ? 'you' : challengerDisplayName.value);
const challengedName = computed(() => userRole.value === 'challenged' ? 'you' : challengedDisplayName.value);
// From the challenge's own team when it carries one, else the loaded standings.
const positionOf = (team) => team?.newPosition
    ?? (ladder.value?.teams ?? []).find(t => t.id === team?.id)?.newPosition
    ?? null;
const challengedPosition = computed(() => positionOf(ladderChallenge.value.challengedTeam));
const challengerPosition = computed(() => positionOf(ladderChallenge.value.challengingTeam));
const isLiveChallenge = computed(() =>
    (challengeStatus.value === 'made' || challengeStatus.value === 'accepted') && playedStatus.value !== 'approved',
);

// top status chip — { label, kind } matching the Flutter _statusChip
const statusChip = computed(() => {
  const isChallenged = userRole.value === 'challenged';
  switch (challengeStatus.value) {
    case 'made':
      return isChallenged
          ? {label: 'Awaiting your response', kind: 'warn'}
          : {label: `Awaiting ${challengedDisplayName.value}`, kind: 'warn'};
    case 'rejected': return {label: 'Declined', kind: 'neutral'};
    case 'cancelled': return {label: 'Cancelled', kind: 'neutral'};
    case 'expired': return {label: 'Expired', kind: 'neutral'};
    // 'skipped' (fixture exists without formal acceptance) falls through to the
    // fixture-derived chips below - "Skipped" told the user nothing useful.
  }
  // accepted
  if (playedStatus.value === 'approved') return {label: 'Played', kind: 'ok'};
  if (playedStatus.value === 'challengingApproved' || playedStatus.value === 'challengedApproved') {
    return {label: 'Awaiting approval', kind: 'warn'};
  }
  // The time's own state (Proposed / Confirmed / ...) is on the When & where
  // card, in the same words as the ladder's challenge rows.
  return {label: 'Accepted', kind: 'ok'};
});

// challenger-first score line, e.g. "3–1 to Grant"
const scoreDisplay = computed(() => {
  const f = fixture.value;
  if (!f || f.homeTeamResult == null || f.awayTeamResult == null) return 'Result recorded';
  const challengerIsHome = ladderChallenge.value.challengingTeamId === f.homeTeamId;
  const cgScore = challengerIsHome ? f.homeTeamResult : f.awayTeamResult;
  const cdScore = challengerIsHome ? f.awayTeamResult : f.homeTeamResult;
  let winner = null;
  if (f.homeTeamPoints != null && f.awayTeamPoints != null && f.homeTeamPoints !== f.awayTeamPoints) {
    const challengerWon = challengerIsHome ? f.homeTeamPoints > f.awayTeamPoints : f.awayTeamPoints > f.homeTeamPoints;
    winner = challengerWon ? challengerDisplayName.value : challengedDisplayName.value;
  }
  return `${cgScore}–${cdScore}${winner ? ` to ${winner}` : ''}`;
});

// In doubles a partner's action is "your side's", not "yours" (same wording
// as the When & where card).
const isMySide = (side) => side === 'challenger' ? userRole.value === 'challenging' : userRole.value === 'challenged';
const sideTeam = (side) => side === 'challenger' ? ladderChallenge.value.challengingTeam : ladderChallenge.value.challengedTeam;
const sideRef = (side) => (isMySide(side) && isTeamSide(sideTeam(side)))
    ? 'your side'
    : (side === 'challenger' ? challengingName.value : challengedName.value);
const sideSubject = (side) => cap(sideRef(side));

// Progress rows (Flutter _ChallengeStatusCard)
const progressRows = computed(() => {
  const c = ladderChallenge.value;
  const cs = challengeStatus.value, ps = playedStatus.value;
  const fixtureWasPlayed = ['challengingApproved', 'challengedApproved', 'approved'].includes(ps);
  const rows = [];

  // 1. challenge made
  rows.push({
    icon: 'mdi-sword-cross', step: 'done',
    text: `${sideSubject('challenger')} challenged ${sideRef('challenged')} on ${dateAt(c.createdAt)}`,
    message: isPlayer.value ? c.requestMessage : null,
  });

  // 2. accept / reject
  if (cs === 'accepted') {
    rows.push({icon: 'mdi-thumb-up', step: 'done', text: `${sideSubject('challenged')} accepted the challenge${c.acceptedAt ? ` on ${dateAt(c.acceptedAt)}` : ''}`, message: isPlayer.value ? c.acceptanceMessage : null});
  } else if (cs === 'rejected') {
    rows.push({icon: 'mdi-thumb-down', step: 'waiting', text: `${sideSubject('challenged')} rejected the challenge${c.rejectedAt ? ` on ${dateAt(c.rejectedAt)}` : ''}`, message: isPlayer.value ? c.rejectionReason : null});
  } else if (cs === 'cancelled') {
    rows.push({icon: 'mdi-close', step: 'waiting', text: 'Challenge has been cancelled'});
  } else if (cs === 'expired') {
    rows.push({icon: 'mdi-close', step: fixtureWasPlayed ? 'done' : 'waiting', text: `This challenge expired on ${dateAt(c.expiresAt)}`});
  } else if (cs === 'made') {
    rows.push({icon: 'mdi-thumbs-up-down', step: 'waiting', text: `Waiting for ${sideRef('challenged')} to respond${c.expiresAt ? ` by ${dateAt(c.expiresAt)}` : ''}`});
  }

  // 3. schedule — same words as the When & where card
  const sc = schedule.value;
  // Same side wording as the When & where card: "Your side" in doubles.
  const nameOf = sideSubject;
  let scheduleText = '', scheduleStep = 'inactive';
  if (cs === 'accepted' || cs === 'skipped') {
    switch (sc.state) {
      case 'S3': scheduleText = 'No time agreed yet'; scheduleStep = 'waiting'; break;
      case 'S4': scheduleText = `${nameOf(sc.proposer)} suggested ${dateAt(sc.when)}, waiting for ${sideRef(sc.proposer === 'challenger' ? 'challenged' : 'challenger')} to confirm`; scheduleStep = 'waiting'; break;
      case 'S4p': scheduleText = `${nameOf(sc.proposer)} suggested ${dateAt(sc.when)}, but that time has passed`; scheduleStep = 'waiting'; break;
      case 'S5': scheduleText = `Confirmed for ${dateAt(sc.when)}, agreed by both sides`; scheduleStep = 'done'; break;
      case 'S6': scheduleText = `Confirmed for ${dateAt(sc.when)}, set by a club admin`; scheduleStep = 'done'; break;
      // S7: played — the result rows say it all; no schedule row.
    }
  }
  if (scheduleText) rows.push({icon: 'mdi-calendar-clock', step: scheduleStep, text: scheduleText});

  // 4. played / result
  let playedText = '', playedStep = 'inactive';
  if (ps === 'inactive') {
    if (cs === 'accepted') { playedText = 'No result has been set for this challenge yet'; playedStep = 'waiting'; }
  } else if (ps === 'pending') {
    playedText = 'No result has been set for this challenge yet'; playedStep = 'waiting';
  } else {
    playedText = scoreDisplay.value; playedStep = 'done';
  }
  if (playedText) rows.push({icon: 'mdi-scoreboard', step: playedStep, text: playedText});

  // 5. approval (skip when ladder auto-approves)
  if (ladder.value?.autoApprove !== true) {
    if (ps === 'challengingApproved') rows.push({icon: 'mdi-check-decagram', step: 'waiting', text: `Waiting for ${sideRef('challenged')} to approve the result`});
    else if (ps === 'challengedApproved') rows.push({icon: 'mdi-check-decagram', step: 'waiting', text: `Waiting for ${sideRef('challenger')} to approve the result`});
    else if (ps === 'approved') rows.push({icon: 'mdi-check-decagram', step: 'done', text: 'Result approved'});
  }

  return rows;
});

// Foot chip. On a swap ladder the loser's destination is the news, so say both.
// A challenge DOWN the ladder (the challenger is ranked above): a challenger win
// changes nothing, and only a win by the lower-ranked side moves anyone.
const isDownwardChallenge = computed(() => {
  const from = challengerPosition.value;
  const to = challengedPosition.value;
  return from != null && to != null && from < to;
});

const winnerChipText = computed(() => {
  const to = challengedPosition.value;
  const from = challengerPosition.value;
  if (isDownwardChallenge.value) {
    const who = userRole.value === 'challenged' ? 'you win' : `${challengedDisplayName.value} wins`;
    return isSwapLadder(ladder.value)
        ? `Only moves if ${who}: ${ordinal(from)} for ${ordinal(to)}`
        : `Only moves if ${who}: ${userRole.value === 'challenged' ? 'you take' : 'they take'} ${ordinal(from)}`;
  }
  if (isSwapLadder(ladder.value) && from != null && from > to) {
    return `Winner takes ${ordinal(to)}, loser ${ordinal(from)}`;
  }
  return `Winner takes ${ordinal(to)}`;
});

// "How it works" dynamic outcome (shown while the challenge is pending)
const howItWorksOutcome = computed(() => {
  const pos = challengedPosition.value;
  const challengerNm = challengerDisplayName.value;
  if (isDownwardChallenge.value) {
    const from = challengerPosition.value;
    const lowerNm = challengedDisplayName.value;
    const swap = isSwapLadder(ladder.value);
    switch (userRole.value) {
      case 'challenging':
        return swap
            ? `If you win nothing changes. If ${lowerNm} wins you swap places: they take ${ordinal(from)}, you take ${ordinal(pos)}`
            : `If you win nothing changes. If ${lowerNm} wins they take ${ordinal(from)} and you drop one place`;
      case 'challenged':
        return swap
            ? `Win and you swap places: you take ${ordinal(from)}, they take ${ordinal(pos)}. If ${challengerNm} wins nothing changes`
            : `Win and you take ${ordinal(from)} and they drop one place. If ${challengerNm} wins nothing changes`;
      default:
        return swap
            ? `If ${challengerNm} wins nothing changes. If ${lowerNm} wins the two swap places`
            : `If ${challengerNm} wins nothing changes. If ${lowerNm} wins they take ${ordinal(from)}`;
    }
  }
  // Swap ladders: the two trade places and nobody else moves, so "they drop
  // one place" would be wrong — the loser goes to where the winner was.
  if (isSwapLadder(ladder.value)) {
    const from = challengerPosition.value;
    switch (userRole.value) {
      case 'challenging':
        return pos != null && from != null ? `Win and you swap places: you take ${ordinal(pos)}, they take ${ordinal(from)}` : 'Win and you swap places with them';
      case 'challenged':
        return pos != null && from != null ? `If ${challengerNm} wins you swap places: they take ${ordinal(pos)}, you take ${ordinal(from)}` : `If ${challengerNm} wins you swap places`;
      default:
        return pos != null && from != null ? `If ${challengerNm} wins the two swap places: ${ordinal(pos)} for ${ordinal(from)}` : `If ${challengerNm} wins the two swap places`;
    }
  }
  switch (userRole.value) {
    case 'challenging':
      return pos != null ? `Win and you take ${ordinal(pos)} and they drop one place` : 'Win and you take their position and they drop one place';
    case 'challenged':
      return pos != null ? `If ${challengerNm} wins they take ${ordinal(pos)} and you drop one place` : `If ${challengerNm} wins they take your position and you drop one place`;
    default:
      const loserNoun = (ladderChallenge.value?.ladder?.playersPerTeam ?? 1) <= 1 ? 'player' : 'team';
      return pos != null ? `If ${challengerNm} wins they take ${ordinal(pos)} and the challenged ${loserNoun} drops one place` : `If ${challengerNm} wins they take the challenged ${loserNoun}'s position and it drops one place`;
  }
});

/* ---- Chat between the challenge's players (same rule as the app's challenge
   page). One other account in the challenge is a direct message; more is the
   challenge's own chat, which the API finds or creates, so whoever presses
   second lands in the chat the first one started. ---- */
const {entityChatLoading, createOrOpenEntityChat, createConversation} = useMessaging();
const directChatLoading = ref(false);
const chatRow = computed(() => {
  if (!isPlayer.value) return null;
  const c = ladderChallenge.value;
  const closed = ['rejected', 'cancelled', 'expired'].includes(challengeStatus.value);
  if (closed && !c.conversationId) return null;

  const withAccount = (team) => (team?.players ?? []).filter(p => p.userId);
  const challenging = userRole.value === 'challenging';
  const opponents = withAccount(challenging ? c.challengedTeam : c.challengingTeam);
  const others = [
    ...withAccount(challenging ? c.challengingTeam : c.challengedTeam).filter(p => p.userId !== user.value?.id),
    ...opponents,
  ];
  const names = others.map(p => (p.firstName ?? '').trim());
  const withNames = names.length === 0 ? null
      : names.length === 1 ? `With ${names[0]}`
      : `With ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

  if (c.conversationId) return {title: 'Open chat', sub: withNames, mode: 'group'};
  if (opponents.length === 0) {
    return {title: 'Chat with players', sub: 'Nobody on the other side has an account yet', mode: null};
  }
  if (others.length === 1) return {title: `Message ${names[0]}`, sub: null, mode: 'direct', userId: others[0].userId};
  return {title: 'Start a group chat', sub: withNames, mode: 'group'};
});
const chatPending = computed(() => entityChatLoading.value || directChatLoading.value);
const openChat = async () => {
  const row = chatRow.value;
  if (!row?.mode || chatPending.value) return;
  if (row.mode === 'group') {
    await createOrOpenEntityChat(
        {ladderChallengeId: ladderChallenge.value.id, conversationId: ladderChallenge.value.conversationId},
        () => getLadderChallenge(ladderChallenge.value.id)
    );
    return;
  }
  try {
    directChatLoading.value = true;
    const conversation = await createConversation([row.userId], 1, null);
    router.push({query: {...route.query, conversationId: conversation.id, messaging: 'open'}});
  } catch (error) {
    setAlertMessage(errorAlert(error, 'Could not open the chat'));
  } finally {
    directChatLoading.value = false;
  }
};

const sentExpiresText = computed(() => {
  const c = ladderChallenge.value;
  const sent = c.createdAt ? `Sent ${outputDateFromDb(c.createdAt, 'ccc d MMM')}` : null;
  // From the challenge's own expiry (ladders set their own number of days).
  // The suggested time now lives on the When & where card.
  const pending = challengeStatus.value === 'made';
  const expires = (c.expiresAt && ['made', 'skipped'].includes(challengeStatus.value))
      ? `expires ${outputDateFromDb(c.expiresAt, 'ccc d MMM, HH:mm')}` + (pending && countdown.value ? ` (${countdown.value})` : '')
      : null;
  return [sent, expires].filter(Boolean).join(' · ');
});

</script>

<template>
  <base-page>
    <!-- Deleted (archived) ladder - show the notice instead of any content -->
    <!-- Members only for this viewer (PRIVACY_PLAN.md §4.1) -->
    <v-container v-if="ladder?.privateToViewer || ladderChallenge?.privateToViewer" class="large">
      <frontend-header class="mb-5" title="Challenge" :sub-title="ladder?.name"
                       :club="ladder?.club ?? (ladder?.clubId ? {id: ladder.clubId, slug: ladder.clubId, name: ladder.clubName} : null)"/>
      <private-notice reason="competition" entity="ladder" :name="ladder?.name" :club-name="ladder?.clubName" :join-slug="ladder?.visibleInviteLink?.slug"
                      :facts="competitionFacts('ladder', ladder, d => outputDateFromDb(d, 'd MMM yyyy'))"/>
    </v-container>
    <v-container v-else-if="ladderArchived" class="large">
      <archived-notice entity="ladder" :name="ladder.name"/>
    </v-container>
    <v-container v-else>

      <!-- ======== ACCEPT / DECLINE DIALOG ======== -->
      <crud-form-dialog
          v-model:dialog="acceptRejectLadderChallengeDialog"
          v-model="ladderChallenge"
          :update-title="dialogTitle"
          action="update"
          :form-pending="ladderChallengeFormPending"
          :btn-text="dialogTitle"
          :btn-color="dialogAccepting ? 'success' : 'error'"
          :btn-icon="dialogAccepting ? 'mdi-thumb-up' : 'mdi-thumb-down'"
          :errors="ladderChallengeFormErrors"
          :save-action="acceptOrRejectLadderChallengeAction">
        <template #default="{errors, item}">
          <div class="pa-4 d-flex align-center mb-4 bg-light rounded" v-if="ladderChallenge.requestMessage">
            <v-icon class="mr-2" icon="mdi-message-text" color="success" size="16"></v-icon>
            <div class="ml-3">
              <div style="font-size:14px;"><strong>Message from challenger:</strong></div>
              <div>{{ ladderChallenge.requestMessage }}</div>
            </div>
          </div>
          <template v-if="dialogAccepting">
            <div class="tl-label mb-2">{{ ladderChallenge.requestMessage ? 'Reply (optional)' : 'Message (optional)' }}</div>
            <v-textarea
                v-model="item.acceptanceMessage"
                :error-messages="ladderChallengeFormErrors.acceptanceMessage"
                :error="ladderChallengeFormErrors.acceptanceMessage != null"
                placeholder="Send a message to the challenger with your acceptance"
                rows="3"/>
            <!-- The challenger proposed a time: an explicit choice, never an
                 agreement by default without seeing it. A time that has
                 passed can't be agreed to. -->
            <div v-if="acceptOptions.hasSuggestion" class="lcd-proposal mt-4">
              <div class="lcd-proposal-t" :class="{'lcd-proposal-t--passed': acceptOptions.suggestionPassed}">
                <v-icon size="16" icon="mdi-calendar-clock" class="mr-1"/>
                {{ cap(challengingName) }} suggested {{ proposedTimeText }}
              </div>
              <div v-if="acceptOptions.passedNote" class="lcd-proposal-note">{{ acceptOptions.passedNote }}</div>
              <v-radio-group v-model="item.timeChoice" hide-details density="compact" class="mt-1">
                <v-radio v-if="acceptOptions.choices.includes('agree')" value="agree" :label="acceptOptions.labels.agree" color="primary"/>
                <v-radio value="other" :label="acceptOptions.labels.other" color="primary"/>
                <v-radio v-if="acceptOptions.choices.includes('later')" value="later" :label="acceptOptions.labels.later" color="primary"/>
              </v-radio-group>
              <div v-if="acceptOptions.hints[item.timeChoice]" class="lcd-proposal-note">{{ acceptOptions.hints[item.timeChoice] }}</div>
            </div>
            <template v-else>
              <v-checkbox
                  v-model="item.suggestDate"
                  :error-messages="ladderChallengeFormErrors.suggestDate"
                  :label="acceptOptions.suggestLabel"
                  class="mt-4"
                  color="primary"
                  hide-details
              />
              <div v-if="item.suggestDate" class="lcd-proposal-note">{{ acceptOptions.suggestHint }}</div>
            </template>
            <template v-if="acceptOptions.hasSuggestion ? item.timeChoice === 'other' : item.suggestDate">
              <simple-date-time-select
                  class="mt-4"
                  v-model:date="item.date"
                  v-model:time="item.time"
                  date-label="Date"
                  :date-error="errors.suggestedDate != null"
                  :date-error-messages="errors.suggestedDate"
              />
              <playing-area-select
                  v-model="item.playingAreaId"
                  :club-id="ladder.clubId || ladderChallenge.clubId"
                  :sport-id="ladder.sportId"
                  :date="item.date"
                  :time="item.time"
              />
            </template>
          </template>
          <template v-else>
            <!-- Declining isn't the only way out of a time that doesn't work. -->
            <v-alert v-if="ladderChallenge.suggestedDate && !acceptOptions.suggestionPassed"
                     class="mb-4" type="info" variant="tonal" density="compact">
              Can't make that time? You don't need to decline. Accept and suggest another time.
            </v-alert>
            <div class="mb-4">
              {{ dialogOnBehalf ? `Decline this challenge on ${challengedDisplayName}'s behalf?` : 'Are you sure you want to decline this challenge?' }}
            </div>
            <v-alert class="mb-4" v-if="ladder.challengeRejectionRule > 0" color="error">
              {{ rejectionPenaltyText(ladder, dialogOnBehalf ? challengedDisplayName : null) }} for declining this challenge.
            </v-alert>
            <div class="tl-label mb-2">Reason (optional)</div>
            <v-textarea
                v-model="item.rejectionReason"
                :error-messages="errors.rejectionReason"
                :error="errors.rejectionReason != null"
                placeholder="Why can't you play this challenge?"
                rows="3"/>
          </template>
          <crud-error-display :errors="ladderChallengeFormErrors"/>
        </template>
      </crud-form-dialog>

      <!-- ======== CHANGE SUGGESTED TIME (challenger, or an admin for them) ======== -->
      <tl-dialog v-model="suggestionDialog" :title="suggestionAction?.label || 'Suggested time'" :max-width="600">
        <p class="text-body-2 text-medium-emphasis mb-3">
          This doesn't accept anything. {{ challengedDisplayName }} still accepts the challenge, and is told about the new time.
        </p>
        <simple-date-time-select
            v-model:date="suggestionForm.date"
            v-model:time="suggestionForm.time"
            date-label="Date"
        />
        <playing-area-select
            v-model="suggestionForm.playingAreaId"
            :club-id="ladder.clubId || ladderChallenge.clubId"
            :sport-id="ladder.sportId"
            :date="suggestionForm.date"
            :time="suggestionForm.time"
        />
        <crud-error-display :errors="suggestionErrors" show-all class="mt-3"/>
        <template #actions>
          <v-btn v-if="suggestionAction?.canRemove && ladderChallenge.suggestedDate"
                 variant="outlined" color="error" :disabled="suggestionPending"
                 @click="saveSuggestion(true)">Remove suggested time</v-btn>
          <v-spacer/>
          <v-btn variant="outlined" :disabled="suggestionPending" @click="suggestionDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" :loading="suggestionPending" @click="saveSuggestion(false)">Save</v-btn>
        </template>
      </tl-dialog>

      <!-- ======== BREADCRUMBS + PAGE HEAD ======== -->
      <frontend-header
          class="mb-5"
          :ladder-challenge="ladderChallenge"
          title="Challenge"
          :sub-title="ladderChallenge.ladderName"
      />

      <!-- ======== CHALLENGE CARD ======== -->
      <div class="tl-card challenge-card mb-4">

        <!-- Meta chips: ladder challenge + status -->
        <div class="challenge-meta">
          <span class="tl-chip">
            <v-icon size="13" class="mr-1">mdi-sword-cross</v-icon>Ladder challenge
          </span>
          <span
              class="tl-chip"
              :class="{ 'tl-chip--warn': statusChip.kind === 'warn', 'tl-chip--ok': statusChip.kind === 'ok' }"
          >
            <v-icon v-if="statusChip.kind === 'warn'" size="13" class="mr-1">mdi-clock-outline</v-icon>
            {{ statusChip.label }}
          </span>
        </div>

        <!-- vs row -->
        <div class="challenge-vs">
          <!-- Challenger -->
          <div class="challenge-player">
            <avatar-image-loader
                :image-file-name="ladderChallenge.challengingTeam.imageFileName || null"
                size="52"
                :image-clickable="false"
                class="challenge-face"
            >
              <template v-if="!ladderChallenge.challengingTeam.imageFileName">
                <span class="challenge-initials">{{ ladderChallenge.challengingTeam.initials }}</span>
              </template>
            </avatar-image-loader>
            <div class="challenge-pname" :class="{ 'challenge-pname--you': userRole === 'challenging' }">
              {{ userRole === 'challenging' ? 'You' : challengerDisplayName }}
            </div>
            <div v-if="isLiveChallenge && ladderChallenge.challengingTeam.newPosition != null" class="challenge-pos">
              {{ ordinal(ladderChallenge.challengingTeam.newPosition) }}
            </div>
          </div>

          <!-- Swords icon -->
          <div class="challenge-sep">
            <v-icon color="primary" size="28">mdi-sword-cross</v-icon>
          </div>

          <!-- Challenged -->
          <div class="challenge-player">
            <avatar-image-loader
                :image-file-name="ladderChallenge.challengedTeam.imageFileName || null"
                size="52"
                :image-clickable="false"
                class="challenge-face"
            >
              <template v-if="!ladderChallenge.challengedTeam.imageFileName">
                <span class="challenge-initials">{{ ladderChallenge.challengedTeam.initials }}</span>
              </template>
            </avatar-image-loader>
            <div class="challenge-pname" :class="{ 'challenge-pname--you': userRole === 'challenged' }">
              {{ userRole === 'challenged' ? 'You' : challengedDisplayName }}
            </div>
            <div v-if="isLiveChallenge && ladderChallenge.challengedTeam.newPosition != null" class="challenge-pos">
              {{ ordinal(ladderChallenge.challengedTeam.newPosition) }}
            </div>
          </div>
        </div>

        <!-- what the match starts on, off both sides' current handicaps -->
        <handicap-match-banner v-if="challengeHandicapStart && !challengeHandicapStart.isLevel"
                               :start="challengeHandicapStart" class="challenge-handicap"/>

        <!-- Foot: sent · expires + winner takes -->
        <div class="challenge-cardfoot" v-if="sentExpiresText || (isLiveChallenge && challengedPosition != null)">
          <span class="challenge-cardfoot-text">{{ sentExpiresText }}</span>
          <span v-if="isLiveChallenge && challengedPosition != null" class="tl-chip tl-chip--brand">
            {{ winnerChipText }}
          </span>
        </div>

        <!-- Action footer: cancel / accept / decline (own, or an admin's on
             behalf) — every one from utils/challengeSchedule.js -->
        <div class="challenge-foot" v-if="canCancel || canAnswer">

          <!-- Cancel (challenger side, or club admin) -->
          <template v-if="canCancel">
            <crud-confirm-dialog
                @confirm="cancelLadderChallengeAction"
                title="Cancel challenge?"
                :message="cancelDeletesMatch
                    ? 'The match that goes with this challenge hasn\'t been played, and it is deleted too, along with its time and court. Cancel the challenge?'
                    : cancelMessage"
                confirmText="Yes"
                cancelText="No"
            >
              <template #default="{click}">
                <v-btn
                    :loading="ladderChallengeFormPending"
                    variant="outlined"
                    color="error"
                    size="small"
                    @click="click"
                >
                  {{ actionLabel('cancel') }}
                </v-btn>
              </template>
            </crud-confirm-dialog>
          </template>

          <div class="challenge-foot-spacer" v-if="canCancel"></div>

          <!-- Accept / Decline (the challenged side, or an admin on their behalf) -->
          <template v-if="canAnswer">
            <v-btn
                variant="outlined"
                color="error"
                size="small"
                @click="acceptLadderChallenge = false"
            >
              {{ actionLabel(answeringOnBehalf ? 'declineOnBehalf' : 'decline') }}
            </v-btn>
            <v-btn
                color="primary"
                size="small"
                @click="acceptLadderChallenge = true"
                prepend-icon="mdi-check"
            >
              {{ actionLabel(answeringOnBehalf ? 'acceptOnBehalf' : 'accept') }}
            </v-btn>
          </template>
        </div>
      </div>

      <!-- ======== WHEN & WHERE — the suggestion before acceptance, then who
           proposed and who confirms (spec R4; same states as the app). First
           thing under the challenge card in every state. ======== -->
      <challenge-when-and-where :schedule="schedule" :confirm-pending="confirmPending"
                                head-class="mb-2" card-class="mb-4" @action="onScheduleAction"/>

      <!-- ======== CHAT — the challenge's players, both sides ======== -->
      <button v-if="chatRow" type="button" class="tl-card chat-row mb-4"
              :class="{'chat-row--disabled': !chatRow.mode}"
              :disabled="!chatRow.mode || chatPending" @click="openChat">
        <span class="chat-row-ic"><v-icon size="20">mdi-message-outline</v-icon></span>
        <span class="chat-row-body">
          <span class="chat-row-t">{{ chatRow.title }}</span>
          <span v-if="chatRow.sub" class="chat-row-s">{{ chatRow.sub }}</span>
        </span>
        <v-progress-circular v-if="chatPending" indeterminate size="16" width="2"/>
        <v-icon v-else-if="chatRow.mode" size="20" class="chat-row-chev">mdi-chevron-right</v-icon>
      </button>

      <!-- ======== INLINE ACTIONS (result / live) — accepted challenges only;
           the time actions are on the When & where card ======== -->
      <div class="challenge-actions mb-4" v-if="canRecordResult">
        <v-btn
            v-if="canLiveScore"
            variant="outlined"
            size="large"
            class="flex-fill"
            prepend-icon="mdi-scoreboard-outline"
            :loading="liveStartPending"
            @click="startLiveScoring"
        >Live score</v-btn>
        <v-btn
            :exact="true"
            :to="resultRoute"
            :color="isChallengeResulted ? undefined : 'primary'"
            :variant="isChallengeResulted ? 'outlined' : 'flat'"
            size="large"
            class="flex-fill"
            :text="resultBtnText"
        />
      </div>

      <!-- ======== MESSAGE (pending, quoted) ======== -->
      <div
          class="tl-card challenge-quote mb-4"
          v-if="challengeStatus === 'made' && isPlayer && ladderChallenge.requestMessage"
      >
        "{{ ladderChallenge.requestMessage }}"
      </div>

      <!-- ======== HOW IT WORKS (pending) / PROGRESS (otherwise) ======== -->
      <template v-if="challengeStatus === 'made'">
        <div class="tl-section-head mb-2">
          <h2>How it works</h2>
        </div>
        <div class="tl-card mb-4">
          <div class="rules-row">
            <v-icon size="19" color="primary">mdi-check</v-icon>
            <span>Accept, then arrange a time between you.</span>
          </div>
          <div class="rules-row">
            <v-icon size="19" color="primary">mdi-scoreboard</v-icon>
            <span>{{ ladder.autoApprove ? 'Play and record the score.' : 'Play, record the score, your opponent approves.' }}</span>
          </div>
          <div class="rules-row">
            <v-icon size="19" color="primary">mdi-ladder</v-icon>
            <span>{{ howItWorksOutcome }}<template v-if="ladder.challengeRejectionRule > 0"> · decline or ignore it: {{ rejectionPenaltyText(ladder, userRole === 'challenged' ? null : challengedDisplayName) }}</template></span>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="tl-section-head mb-2">
          <h2>Progress</h2>
        </div>
        <div class="tl-card status-card mb-4">
          <div
              v-for="(row, i) in progressRows"
              :key="i"
              class="status-row"
              :class="{ 'status-row--divider': i > 0 }"
          >
            <span class="status-ic" :class="`status-ic--${row.step}`">
              <v-icon size="16">{{ row.icon }}</v-icon>
            </span>
            <div class="status-body">
              <div class="status-text">{{ row.text }}</div>
              <div v-if="row.message" class="status-msg">"{{ row.message }}"</div>
            </div>
          </div>
        </div>
      </template>

      <!-- ======== MATCH RESULT ======== -->
      <template v-if="isChallengeResulted && ladderChallenge.fixture">
        <div class="tl-section-head mb-2">
          <h2>Match result</h2>
        </div>
        <fixture-cell :fixture="ladderChallenge.fixture" size="large" :navigable="true" :allow-editing="false" class="mb-4"/>
      </template>

      <!-- ======== AI ANALYSIS ======== -->
      <template v-if="ladderChallenge.analysis && ladderChallenge.chanceOfSuccess">
        <div class="tl-section-head mb-2">
          <h2>Match insight</h2>
        </div>
        <div class="ai-card mb-4">
          <div class="ai-head"><v-icon size="16" icon="mdi-creation"/>AI analysis</div>

          <!-- Chance of success — colour-coded by the model's estimate -->
          <div
              class="ai-chance"
              :class="ladderChallenge.chanceOfSuccess >= 55 ? 'ai-chance--success' : (ladderChallenge.chanceOfSuccess > 45 ? 'ai-chance--info' : 'ai-chance--error')"
          >
            <v-icon size="15" icon="mdi-thumbs-up-down"/>
            <span class="ai-chance-label">Chance of success</span>
            <span class="ai-chance-value">{{ ladderChallenge.chanceOfSuccess }}%</span>
          </div>

          <p class="mb-0">{{ ladderChallenge.analysis }}</p>
        </div>
      </template>

      <!-- ======== CURRENT LADDER STANDINGS ======== -->
      <div class="tl-section-head mb-2">
        <h2>Current ladder standings</h2>
      </div>
      <div class="tl-card pa-3 mb-4">
        <ladder-view
            :simple="true"
            :highlighted-team-ids="[ladderChallenge.challengingTeamId, ladderChallenge.challengedTeamId]"
            :ladder="ladder"
        />
      </div>

    </v-container>
  </base-page>
</template>

<style scoped lang="scss">
/* the challenger's proposed time, inside the accept dialog */
.lcd-proposal {
  padding: 12px 14px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
  background: rgb(var(--v-theme-light));
}

.lcd-proposal-t {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 700;

  &--passed {
    color: rgb(var(--v-theme-textSubtle));
    text-decoration: line-through;
  }
}

.lcd-proposal-note {
  font-size: 12px;
  line-height: 1.5;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 6px;
}

.challenge-handicap { margin: 12px 16px 4px; }
/* AI insight card (matches the fixture-detail .ai-card) */
.ai-card {
  border: 1px solid #E3DBF4;
  background: linear-gradient(160deg, #FBFAFE 0%, #F6F2FC 100%);
  border-radius: 14px;
  padding: 14px 16px;
}

.ai-card .ai-head {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 700;
  color: #7C5CC4;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 10px;
}

.ai-card p {
  font-size: 12.5px;
  color: rgb(var(--v-theme-charcoal));
  line-height: 1.65;
  white-space: pre-line;
}

/* Chance-of-success pill — colour-coded by the model's estimate */
.ai-chance {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: 999px;
  margin-bottom: 12px;
}

.ai-chance-value {
  font-weight: 800;
}

.ai-chance--success {
  background: rgb(var(--v-theme-successTint));
  color: rgb(var(--v-theme-success));
}

.ai-chance--info {
  background: rgba(var(--v-theme-info), 0.12);
  color: rgb(var(--v-theme-info));
}

.ai-chance--error {
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-error));
}

/* ---- Challenge card ---- */
.challenge-card {
  padding: 16px;
}

.challenge-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

/* status / context chips render as pills (global .tl-chip only sets colour) */
.tl-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
}

/* vs row */
.challenge-vs {
  display: flex;
  align-items: center;
  max-width: 560px;
  margin: 0 auto 14px;
}

.challenge-player {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.challenge-face {
  position: relative;
}

.challenge-initials {
  font-size: 16px;
  font-weight: 700;
  color: rgb(var(--v-theme-textSubtle));
}

.challenge-pname {
  font-size: 12.5px;
  font-weight: 600;
  text-align: center;

  &--you {
    color: rgb(var(--v-theme-primary));
  }
}

.challenge-pos {
  font-size: 11px;
  color: rgb(var(--v-theme-textSubtle));
}

.challenge-sep {
  display: flex;
  align-items: center;
  padding: 0 12px;
}

/* card foot: sent · expires + winner-takes chip */
.challenge-cardfoot {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}

.challenge-cardfoot-text {
  flex: 1;
  min-width: 0;
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
}

/* inline primary/secondary action buttons */
.challenge-actions {
  display: flex;
  gap: 10px;
}

/* chat row */
.chat-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  text-align: left;
  cursor: pointer;

  &--disabled {
    opacity: 0.5;
    cursor: default;
  }
}

.chat-row-ic {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgb(var(--v-theme-bg, 250, 250, 250));
  color: rgb(var(--v-theme-charcoal));
}

.chat-row-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.chat-row-t {
  font-size: 13.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
}

.chat-row-s {
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
}

.chat-row-chev {
  color: rgb(var(--v-theme-textSubtle));
}

/* quoted request message (pending) */
.challenge-quote {
  padding: 12px 14px;
  font-size: 12.5px;
  font-style: italic;
  line-height: 1.65;
  color: rgb(var(--v-theme-textSubtle));
}

/* ---- Progress status card (Flutter _ChallengeStatusCard) ---- */
.status-card {
  padding: 6px 0;
}

.status-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 11px 16px;

  &--divider {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }
}

.status-ic {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &--done {
    background: rgb(var(--v-theme-successTint));
    color: rgb(var(--v-theme-success));
  }

  &--waiting {
    background: rgb(var(--v-theme-warningTint));
    color: rgb(var(--v-theme-warning));
  }

  &--inactive {
    background: rgb(var(--v-theme-bg, 250, 250, 250));
    color: rgb(var(--v-theme-textSubtle));
  }

  .v-icon {
    color: inherit;
  }
}

.status-body {
  flex: 1;
  min-width: 0;
  padding-top: 4px;
}

.status-text {
  font-size: 12.5px;
  color: rgb(var(--v-theme-charcoal));
  line-height: 1.5;
}

.status-msg {
  font-size: 12px;
  font-style: italic;
  color: rgb(var(--v-theme-textSubtle));
  line-height: 1.5;
  margin-top: 4px;
}

/* footer */
.challenge-foot {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 10px;
  flex-wrap: wrap;
}

.challenge-foot-spacer {
  flex: 1;
}

/* ---- Checkrow (suggested time) ---- */
.checkrow {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 16px;
}

.checkrow-who {
  flex: 1;
}

.checkrow-t {
  font-size: 13px;
  font-weight: 600;
}

.checkrow-s {
  font-size: 11px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 2px;
  line-height: 1.5;
}

/* ---- How it works rows ---- */
.rules-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 16px;
  font-size: 12px;
  color: rgb(var(--v-theme-charcoal));
  line-height: 1.5;

  & + .rules-row {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }
}
</style>
